import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Link,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import axios from "axios";
import { toast } from "react-toastify";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import StyledInputField from "../Components/Custom/StyledInputField";
import MUITextField from "../Components/MUITextField";
import MUITypography from "../Components/MUITypography";
import Loader from "../Components/Loader";
import ConfirmationDialog from "../Components/ConfirmationDialog";
import OTPDialog from "./Registration/OTPDialog";
import SubscriptionGateDialog from "../Util/SubscriptionGateDialog";
import {
  getSubscriptionGateFromResponse,
  SUBSCRIPTION_ACTION_UPGRADE,
} from "../Util/subscriptionLifecycleHelper";

import StorageFactory from "../Util/StorageFactory";
import { ensureWebFcmToken } from "../Util/FCM/FirebaseMessaging";
import { isAdminLoggedIn, isEmpty, isAdminPanelLogin, normalizeLoginPermissions, toTitleCase } from "../Util/UtilHelper";
import {
  APP_NAME,
  ROUTE_DELETE_ACCOUNT,
  ROUTE_FORGOT_PASSWORD,
  ROUTE_MAIN_HOME,
  ROUTE_REGISTRATION,
  ROUTE_SUBSCRIPTION,
  ROUTE_SUPER_ADMIN,
  SESSION_ACCOUNT_DELETION_PENDING_KEY,
  SESSION_ACCOUNT_DELETION_DATA_KEY,
  SESSION_USER_COUNTRY_CODE_KEY,
  SESSION_USER_COUNTRY_DATA_KEY,
  SESSION_USER_CREATED_BY_KEY,
  SESSION_USER_ID_KEY,
  SESSION_USER_IMAGE_KEY,
  SESSION_USER_MOBILE_NO_KEY,
  SESSION_USER_NAME_KEY,
  SESSION_LOGIN_NAME_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
  SESSION_IS_SUPER_ADMIN_KEY,
  SESSION_ADMIN_TYPE_KEY,
  SESSION_ADMIN_SCOPE_KEY,
  SESSION_USER_TYPE_KEY,
  OTP_VERIFIED_LOGIN_AGAIN,
} from "../Util/VMConstants";

import { VM_Colors } from "../Styles/VMStyles";

import { API_RESEND_OTP, API_USER_INFO, API_USER_LOGIN } from "../Util/URLConstants";
import { saveAuthFromResponse } from "../Util/jwtAuth";
import {
  getLoginDeletionBlockDialog,
  isPendingDeletionStatus,
  resolveLoginDeletionOutcome,
  setAccountDeletionSession,
} from "../Services/accountDeletionService";

const persistLoginSession = (sessionFactory, respData) => {
  saveAuthFromResponse(respData);
  if (!respData?.user?.user_id) {
    return { isAdminPanel: false, adminType: null };
  }
  sessionFactory.add(SESSION_USER_ID_KEY, respData.user.user_id);
  sessionFactory.add(SESSION_USER_NAME_KEY, respData.user.name);
  sessionFactory.add(
    SESSION_LOGIN_NAME_KEY,
    respData.user.login_name ?? respData.user.user_name ?? respData.user.email ?? ""
  );
  sessionFactory.add(SESSION_USER_TYPE_KEY, respData.user.user_type);
  sessionFactory.add(SESSION_USER_COUNTRY_CODE_KEY, respData.user.country_code);
  sessionFactory.add(SESSION_USER_COUNTRY_DATA_KEY, respData.user.country_data);
  sessionFactory.add(SESSION_USER_MOBILE_NO_KEY, respData.user.mobile_no);
  sessionFactory.add(SESSION_USER_IMAGE_KEY, respData.user.image_url);
  sessionFactory.add(
    SESSION_USER_CREATED_BY_KEY,
    isAdminLoggedIn(respData.user.user_type) ? "" : respData.user.created_by
  );

  const updatedPermissions = normalizeLoginPermissions(respData.user.permissions);
  sessionFactory.add(SESSION_USER_ROLES_PERMISSIONS, updatedPermissions);

  const isAdminPanel = isAdminPanelLogin(respData);
  const adminType = respData.admin_type || (isAdminPanel ? "SuperAdmin" : null);
  sessionFactory.add(SESSION_IS_SUPER_ADMIN_KEY, isAdminPanel);
  if (adminType) sessionFactory.add(SESSION_ADMIN_TYPE_KEY, adminType);
  if (respData.admin_scope) sessionFactory.add(SESSION_ADMIN_SCOPE_KEY, respData.admin_scope);

  return { isAdminPanel, adminType };
};

const navigateAfterLogin = (navigate, { isAdminPanel, adminType, respData, deletionOutcome }) => {
  if (isAdminPanel) {
    localStorage.setItem("superAdminId", respData.user.user_id);
    localStorage.setItem("superAdminUser", JSON.stringify(respData.user));
    if (adminType) localStorage.setItem("adminType", adminType);
    if (respData.admin_scope) {
      localStorage.setItem("adminScope", JSON.stringify(respData.admin_scope));
    }
    navigate("/" + ROUTE_SUPER_ADMIN, { replace: true });
    return;
  }

  if (deletionOutcome.type === "pending_login") {
    navigate("/" + ROUTE_DELETE_ACCOUNT, { replace: true });
    return;
  }

  navigate("/" + ROUTE_MAIN_HOME, { replace: true });
};

/** Normalize incomplete-registration payload from login (venue / subvenue / otp). */
const buildRegisterPending = (respData) => {
  if (!respData?.pending) return null;
  const pendingData = respData.pendingdata || {};
  return {
    ...pendingData,
    pending: pendingData.pending || respData.pending,
    user_id: pendingData.user_id,
    venue_id: pendingData.venue_id,
  };
};

const isAdminUserType = (userType) =>
  isAdminLoggedIn(userType) ||
  userType === "SuperAdmin" ||
  userType === "subAdmin";

/** Resolve user_type from login pending payload, or look it up when missing. */
const getPendingUserType = async (registerPending, respData) => {
  const direct =
    registerPending?.user_type ||
    respData?.user?.user_type ||
    respData?.user_type ||
    "";
  if (direct) return direct;

  const userId = registerPending?.user_id;
  if (!userId) return "";

  try {
    const { data } = await axios.get(`${API_USER_INFO}?user_id=${userId}`);
    return data?.user_info?.user_type || "";
  } catch {
    return "";
  }
};

function VMLogin() {
  const navigate = useNavigate();
  const sessionFactory = useMemo(() => new StorageFactory("session"), []);

  const [state, setState] = useState({
    username: "",
    password: "",
    loading: false,
    openConfirm: false,
    confirmationMSG: "",
    registerPending: "",
  });

  const [errors, setErrors] = useState({
    username: false,
    password: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [fcmtoken, setFcmToken] = useState("");
  const [upgradeGate, setUpgradeGate] = useState({
    open: false,
    message: "",
    action: SUBSCRIPTION_ACTION_UPGRADE,
  });
  const [deletionBlock, setDeletionBlock] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [loginOtp, setLoginOtp] = useState({
    open: false,
    userId: "",
    verifiedPrompt: false,
  });

  /* ================= CLEAR APP CACHE ================= */
  const clearAppCache = async () => {
    try {
      sessionFactory.clear();

      if (typeof window !== "undefined") {
        localStorage.clear();
      }

      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      setFcmToken("");
      console.log("✅ App cache cleared");
    } catch (err) {
      console.error("❌ Cache clear error", err);
    }
  };

  /* ================= FCM TOKEN (prefetch if already granted) ================= */
  useEffect(() => {
    if (!("Notification" in window) || Notification.permission !== "granted") {
      return;
    }

    ensureWebFcmToken({ requestPermission: false })
      .then((token) => {
        if (token) setFcmToken(token);
      })
      .catch(() => {});
  }, []);

  /* ================= LOGIN ================= */
  const submitHandler = async () => {
    await clearAppCache();

    const newErrors = {
      username: !state.username,
      password: !state.password,
    };
    setErrors(newErrors);

    if (newErrors.username || newErrors.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));

    let token = fcmtoken;
    try {
      token =
        (await ensureWebFcmToken({ requestPermission: true })) || fcmtoken || "";
      if (token) setFcmToken(token);
    } catch (_) {
      /* login continues without push if permission denied */
    }

    try {
      const response = await axios.post(
        API_USER_LOGIN,
        {
          username: state.username,
          password: state.password,
          firebase_token: token,
          platform: "web",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const respData = response.data;
      setState((prev) => ({ ...prev, loading: false }));
      saveAuthFromResponse(respData);

      const deletionOutcome = resolveLoginDeletionOutcome(respData);
      const deletionDialog = getLoginDeletionBlockDialog(deletionOutcome, respData);

      if (deletionDialog) {
        setDeletionBlock({ open: true, ...deletionDialog });
        return;
      }

      if (respData.status_code === 200) {
        const { isAdminPanel, adminType } = persistLoginSession(sessionFactory, respData);

        if (deletionOutcome.type === "pending_login") {
          setAccountDeletionSession(sessionFactory, {
            pending: true,
            data: deletionOutcome.data,
          });
        } else {
          setAccountDeletionSession(sessionFactory, { pending: false });
        }

        navigateAfterLogin(navigate, {
          isAdminPanel,
          adminType,
          respData,
          deletionOutcome,
        });
        return;
      }

      const loginGate = getSubscriptionGateFromResponse(respData);
      if (loginGate) {
        if (respData?.user?.user_id) {
          persistLoginSession(sessionFactory, respData);
        }
        setUpgradeGate({
          open: true,
          message: loginGate.message,
          action: loginGate.action,
        });
        return;
      }

      if (await handleRegisterPending(respData)) return;

      toast.error(respData.message || "Login failed");
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false }));
      const respData = error?.response?.data;
      saveAuthFromResponse(respData);

      const deletionOutcome = resolveLoginDeletionOutcome(respData);
      const deletionDialog = getLoginDeletionBlockDialog(deletionOutcome, respData);

      if (deletionDialog) {
        setDeletionBlock({ open: true, ...deletionDialog });
        return;
      }

      const loginGate = getSubscriptionGateFromResponse(respData);
      if (loginGate) {
        if (respData?.user?.user_id) {
          persistLoginSession(sessionFactory, respData);
        }
        setUpgradeGate({
          open: true,
          message: loginGate.message,
          action: loginGate.action,
        });
        return;
      }

      if (await handleRegisterPending(respData)) return;

      toast.error(respData?.message || "Something went wrong!");
    }
  };

  /* ================= AUTO REDIRECT ================= */
  useEffect(() => {
    const sessionUserId = sessionFactory.get(SESSION_USER_ID_KEY);
    if (isEmpty(sessionUserId)) {
      return;
    }

    const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
    const adminType = sessionFactory.get(SESSION_ADMIN_TYPE_KEY);
    const isAdminPanel =
      sessionFactory.get(SESSION_IS_SUPER_ADMIN_KEY) === true ||
      adminType === "SuperAdmin" ||
      adminType === "subAdmin" ||
      userType === "SuperAdmin" ||
      userType === "subAdmin";
    if (isAdminPanel) {
      navigate("/" + ROUTE_SUPER_ADMIN, { replace: true });
      return;
    }

    if (sessionFactory.get(SESSION_ACCOUNT_DELETION_PENDING_KEY) === true) {
      navigate("/" + ROUTE_DELETE_ACCOUNT, { replace: true });
      return;
    }

    const cachedDeletion = sessionFactory.get(SESSION_ACCOUNT_DELETION_DATA_KEY);
    if (cachedDeletion && isPendingDeletionStatus(cachedDeletion.status)) {
      navigate("/" + ROUTE_DELETE_ACCOUNT, { replace: true });
      return;
    }

    navigate("/" + ROUTE_MAIN_HOME, { replace: true });
  }, [navigate, sessionFactory]);

  const openTeamMemberOtpDialog = async (userId) => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      await axios.post(API_RESEND_OTP, { user_id: userId });
    } catch (_) {
      /* still open the dialog so the user can resend from there */
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
      setLoginOtp({ open: true, userId, verifiedPrompt: false });
    }
  };

  const handleRegisterPending = async (respData) => {
    const registerPending = buildRegisterPending(respData);
    if (respData?.status_code !== 400 || !registerPending) return false;
    saveAuthFromResponse(respData);

    if (registerPending.pending === "otp" && registerPending.user_id) {
      setState((prev) => ({ ...prev, loading: true }));
      const userType = await getPendingUserType(registerPending, respData);
      if (!isAdminUserType(userType)) {
        await openTeamMemberOtpDialog(registerPending.user_id);
        return true;
      }
      setState((prev) => ({ ...prev, loading: false }));
    }

    setState((prev) => ({
      ...prev,
      openConfirm: true,
      confirmationMSG:
        respData.message ||
        "Registration not completed. Please continue registration.",
      registerPending,
    }));
    return true;
  };

  const handleLoginOtpVerified = () => {
    setLoginOtp({
      open: false,
      userId: "",
      verifiedPrompt: true,
    });
  };

useEffect(() => {
  // Replace current history entry
  window.history.replaceState(null, "", window.location.href);

  const blockNavigation = () => {
    // Always stay on login
    window.history.pushState(null, "", window.location.href);
  };

  // Push dummy state to block back & forward
  window.history.pushState(null, "", window.location.href);
  window.addEventListener("popstate", blockNavigation);

  return () => {
    window.removeEventListener("popstate", blockNavigation);
  };
}, []);


  /* ================= UI ================= */
  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          backgroundColor: "#fff",
          borderRadius: 2,
          p: { xs: 3, sm: 4 },
          boxShadow: 3,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <Avatar src={'/static/logo.png'} sx={{ width: 48, height: 48 }}>
            <LocationCityIcon />
          </Avatar>
          <Box component="img" src={'/static/img_logo_text_1_1.png'} alt={APP_NAME} height={30} />
        </Box>

        <Typography
          variant="h1"
          sx={{ fontWeight: 700, textAlign: "center", mb: 3 }}
        >
          Login
        </Typography>

        {/* Email */}
        <Box mb={2}>
          <StyledInputField
            heading="Email ID"
            placeholder={toTitleCase("Enter your email address")}
            error={errors.username}
            helperText={errors.username ? "Email is required" : ""}
            onChange={(e) => {
              setState((prev) => ({ ...prev, username: e.target.value }));
              setErrors((prev) => ({ ...prev, username: false }));
            }}
          />
        </Box>

        {/* Password */}
        <Box mb={2}>
          <MUITypography>Password</MUITypography>
          <MUITextField
            fullWidth
            type={showPassword ? "text" : "password"}
            placeholder={toTitleCase("Enter your password")}
            value={state.password}
            error={errors.password}
            helperText={errors.password ? "Password is required" : ""}
            onChange={(e) => {
              setState((prev) => ({ ...prev, password: e.target.value }));
              setErrors((prev) => ({ ...prev, password: false }));
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box textAlign="right" mb={2}>
          <Link
            component={RouterLink}
            to={`/${ROUTE_FORGOT_PASSWORD}`}
            sx={{ fontWeight: 600, fontSize: 14 }}
          >
            Forgot Password?
          </Link>
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={submitHandler}
          fullWidth
          sx={{
            fontSize: 16,
            py: 1,
            borderRadius: 1,
          }}
        >
          Login
        </Button>

        <Box textAlign="center" mt={3}>
          <Typography display="inline">Don’t have an account?</Typography>
          <Button
            onClick={() =>
              navigate("/" + ROUTE_REGISTRATION, { replace: true })
            }
            sx={{
              fontWeight: 700,
              color: VM_Colors.secondary,
              fontSize: 18,
              textTransform: "none",
              // pl: 2,
              minWidth: "unset",
              "&:hover": {
                textDecoration: "underline",
                backgroundColor: "transparent",
              },
            }}
          >
            Sign Up
          </Button>
        </Box>
      </Box>

      <Loader loading={state.loading} />

      <ConfirmationDialog
        open={deletionBlock.open}
        title={deletionBlock.title}
        message={deletionBlock.message}
        okOnly
        onClose={() =>
          setDeletionBlock({
            open: false,
            title: "",
            message: "",
          })
        }
        onConfirm={() =>
          setDeletionBlock({
            open: false,
            title: "",
            message: "",
          })
        }
      />

      <ConfirmationDialog
        open={state.openConfirm}
        title="Alert"
        message={state.confirmationMSG}
        okOnly
        onClose={() => {
          const pending = state.registerPending;
          setState((prev) => ({
            ...prev,
            openConfirm: false,
            confirmationMSG: "",
            registerPending: "",
          }));
          // OK → resume registration at venue (step 2) or subvenue (step 3)
          if (
            pending?.pending === "venue" ||
            pending?.pending === "subvenue" ||
            pending?.pending === "otp"
          ) {
            navigate("/" + ROUTE_REGISTRATION, {
              state: { registerPending: pending },
            });
          }
        }}
      />

      <OTPDialog
        showOtpDialog={loginOtp.open}
        handleClose={() =>
          setLoginOtp((prev) =>
            prev.verifiedPrompt ? prev : { ...prev, open: false }
          )
        }
        arrived="login"
        user_id={loginOtp.userId}
        handleOTPVerify={handleLoginOtpVerified}
      />

      <ConfirmationDialog
        open={loginOtp.verifiedPrompt}
        title="Alert"
        message={OTP_VERIFIED_LOGIN_AGAIN}
        okOnly
        onClose={() =>
          setLoginOtp({ open: false, userId: "", verifiedPrompt: false })
        }
      />

      <SubscriptionGateDialog
        open={upgradeGate.open}
        onClose={() =>
          setUpgradeGate((prev) => ({ ...prev, open: false }))
        }
        message={upgradeGate.message}
        action={upgradeGate.action}
        onConfirm={() => {
          setUpgradeGate((prev) => ({ ...prev, open: false }));
          navigate("/" + ROUTE_SUBSCRIPTION);
        }}
      />
    </>
  );
}

export default VMLogin;
